import {
  Box,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Select,
  Textarea,
  VStack
} from '@chakra-ui/react';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormInputProps } from 'src/@types';
import { AppToolTip } from './../../general';
import { validationMessagesTranslated } from '../../validation/validation-messages';
import { formatValue, handleInput, handleWheelEvent, keyDown } from '../form-elements-factory';
import { useIntl } from 'react-intl';
export const DynamicInput: FunctionComponent<FormInputProps> = ({
  type = 'text',
  label,
  layout = 'vertical',
  defaultValue,
  id,
  placeholder,
  register,
  validation,
  variant,
  tooltip,
  removeDecimals,
  error,
  size = 'regular', // 'regular' | 'wide
  children,
  options,
  values,
  optionalStyle,
  hint
}) => {
  let component;
  const intl = useIntl();
  useEffect(() => {
    document.addEventListener('wheel', handleWheelEvent);
    return () => {
      document.removeEventListener('wheel', handleWheelEvent);
    };
  }, []);

  if (type === 'date' && validation) {
    validation['pattern'] = {
      value: /^\d{4}-\d{2}-\d{2}$/,
      message: validationMessagesTranslated(intl).date
    };
  }

  const finalValidation = validation;

  switch (type) {
    case 'checkbox':
      component = (
        <CheckboxGroup defaultValue={[]}>
          <VStack spacing={2} align="flex-start">
            {options?.map(({ label, value }) => (
              <Checkbox
                size="xl"
                key={value}
                id={id}
                value={value}
                {...register(id, {
                  ...(finalValidation ? finalValidation : {})
                })}>
                <span dangerouslySetInnerHTML={{ __html: label }}></span>
              </Checkbox>
            ))}
          </VStack>
        </CheckboxGroup>
      );
      break;
    case 'select':
      component = (
        <Select
          id={id}
          placeholder={placeholder}
          defaultValue={defaultValue}
          {...register(id, {
            ...(finalValidation ? finalValidation : {})
          })}>
          {options?.map(({ label, value }, index) => (
            <option key={index} value={value} dangerouslySetInnerHTML={{ __html: label }}></option>
          ))}
        </Select>
      );
      break;

    case 'radio':
      component = (
        <RadioGroup value={defaultValue} mt={size === 'wide' ? 2 : 0}>
          {layout === 'vertical' ? (
            <VStack spacing={2} align="flex-start">
              {options?.map(({ label, value }) => (
                <Radio
                  size={size}
                  key={value}
                  id={id}
                  name={id}
                  value={value}
                  {...register(id, {
                    ...(finalValidation ? finalValidation : {})
                  })}>
                  {label}
                </Radio>
              ))}
            </VStack>
          ) : (
            <HStack spacing={2} align="flex-start">
              {options?.map(({ label, value }) => (
                <Radio
                  size={size}
                  key={value}
                  id={id}
                  name={id}
                  value={value}
                  {...register(id, {
                    ...(finalValidation ? finalValidation : {})
                  })}>
                  {label}
                </Radio>
              ))}
            </HStack>
          )}
        </RadioGroup>
      );
      break;
    case 'textarea':
      component = (
        <Textarea
          bgColor={'white'}
          autoComplete={'off'}
          id={id}
          rows={4}
          onInput={(e: React.FormEvent<HTMLInputElement>) => handleInput(e, type, finalValidation)}
          placeholder={placeholder}
          defaultValue={defaultValue}
          {...register(id, {
            ...(finalValidation ? finalValidation : {})
          })}
        />
      );
      break;
    case 'currency':
    case 'percentage':
      component = (
        <Input
          onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
            formatValue(e, 'focus', type, values, type === 'currency' ? removeDecimals : false)
          }
          type={'text'}
          autoComplete={'off'}
          id={id}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => keyDown(e, type)}
          placeholder={!placeholder ? (type === 'currency' ? '€' : '%') : placeholder}
          defaultValue={defaultValue}
          {...register(id, {
            ...(finalValidation ? finalValidation : {}),
            ...{
              onBlur: (event: React.ChangeEvent<HTMLInputElement>) =>
                formatValue(
                  event,
                  'blur',
                  type,
                  values,
                  type === 'currency' ? removeDecimals : false
                )
            }
          })}
        />
      );
      break;
    default:
      component = (
        <Input
          type={type}
          id={id}
          background={'white'}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => keyDown(e, type)}
          placeholder={placeholder}
          defaultValue={defaultValue}
          {...register(id, {
            ...(finalValidation ? finalValidation : {})
          })}
        />
      );
      break;
  }
  return (
    <FormControl
      variant={variant}
      isInvalid={error}
      isRequired={finalValidation?.required as boolean}
      py={optionalStyle ? 4 : 2}
      px={optionalStyle ? 4 : undefined}
      bgColor={optionalStyle ? 'gray.100' : undefined}>
      {label && (
        <FormLabel htmlFor={id} variant={variant}>
          {label}
        </FormLabel>
      )}
      {tooltip && <AppToolTip text={tooltip}></AppToolTip>}
      {children}
      {component}
      {error && <FormErrorMessage>{error?.message}</FormErrorMessage>}
      {hint && <Box fontSize={'sm'} pt={2} dangerouslySetInnerHTML={{ __html: hint }}></Box>}
    </FormControl>
  );
};
